import { useMemo, useState } from "react";

import { useSelectionStore } from "../../state/useSelectionStore";
import { useLayerStore } from "../../state/useLayerStore";
import {
  HORIZON_YEARS,
  useDemographicsStore,
} from "../../state/useDemographicsStore";
import { useAreaDemographics, useCrossSection } from "../../lib/useDemographics";
import {
  SCENARIO_LABELS,
  strengthLabel,
  type ProjectionScenario,
} from "../../lib/demographics";
import { countShort, num, pct } from "../../lib/format";
import { levelDef } from "../../map/levels";
import {
  CENSUS_PERIODS,
  MAP_METRICS,
  periodLabel,
  type MapMetricId,
} from "../../map/layers/demographics";
import { Choice, Figure, SectionTitle, Select } from "../ui/Field";
import { ComponentsChart, ComponentsKey } from "./ComponentsChart";
import { PopulationChart, type PopulationView } from "./PopulationChart";
import { CorrelationScatter } from "./CorrelationScatter";
import { X_METRICS, Y_METRICS, xMetric, yMetric } from "./metrics";

const DEPARTMENTS = [
  { value: "all", label: "All of Île-de-France" },
  { value: "75", label: "Paris" },
  { value: "77", label: "Seine-et-Marne" },
  { value: "78", label: "Yvelines" },
  { value: "91", label: "Essonne" },
  { value: "92", label: "Hauts-de-Seine" },
  { value: "93", label: "Seine-Saint-Denis" },
  { value: "94", label: "Val-de-Marne" },
  { value: "95", label: "Val-d'Oise" },
];

/**
 * Who lives here, where that is going, and whether prices have noticed.
 *
 * Three questions in one pane, in the order they have to be asked. The census
 * says what happened and splits it into the part driven by an age pyramid and
 * the part driven by people moving; the projection carries the second one
 * forward under assumptions the user sets; and the scatter checks the whole
 * story against what flats actually sold for.
 *
 * Nothing here predicts a commune. INSEE does not publish a projection below
 * department level, and the one built here is a department trajectory times an
 * area's own recent divergence, faded out. That is stated on screen, once.
 */
export function DemographicsPanel({ visible }: { visible: boolean }) {
  const selection = useSelectionStore((s) => s.selection);
  const s = useDemographicsStore();
  const [view, setView] = useState<PopulationView>("people");

  const area = useMemo(
    () =>
      selection ? { scope: selection.scope, code: selection.code } : null,
    [selection],
  );
  const params = useMemo(
    () => ({ scenario: s.scenario, persistence: s.persistence, toYear: s.toYear }),
    [s.scenario, s.persistence, s.toYear],
  );

  const { data, history, projection, isLoading, isError } = useAreaDemographics(
    area,
    params,
  );
  const cross = useCrossSection(s.correlationX, s.correlationY, {
    minPop: s.minPop,
    dep: s.depFilter,
  });

  const latest = data?.series[data.series.length - 1] ?? null;
  const here = cross.points.find((p) => p.code === data?.code) ?? null;
  const mx = xMetric(s.correlationX);
  const my = yMetric(s.correlationY);

  return (
    <div className="px-4 py-3">
      <MapControls metric={s.metric} period={s.period} />

      <div className="mt-4 border-t border-line pt-3">
        {data === null ? (
          <p className="rounded-lg bg-surface-alt px-3 py-3 text-center text-[12px] leading-snug text-ink-soft">
            {isLoading
              ? "Loading…"
              : isError
                ? "No census series for this area."
                : "Click an area on the map."}
          </p>
        ) : (
          <>
            <header className="mb-3">
              <h3 className="truncate text-[13px] font-semibold">{data.name}</h3>
              <p className="text-[10px] text-ink-soft">
                {levelDef(data.scope).noun}
                {latest && ` · ${num(latest.pop)} people in ${latest.year}`}
                {data.resolved_from === "iris" &&
                  " · the census does not go below city level"}
              </p>
            </header>

            {latest && (
              <div className="grid grid-cols-3 gap-2">
                <Figure
                  label={`Since ${history.at(-1)?.from ?? ""}`}
                  value={`${pct(latest.growth_rate ?? 0)}`}
                  sub="a year"
                  accent
                />
                <Figure
                  label="Migration"
                  value={pct(latest.migration_rate ?? 0)}
                  sub="arriving − leaving"
                />
                <Figure
                  label="Natural"
                  value={pct(latest.natural_rate ?? 0)}
                  sub="births − deaths"
                />
              </div>
            )}

            {visible && history.length > 0 && (
              <div className="mt-3">
                <SectionTitle>What drove it, census by census</SectionTitle>
                <ComponentsChart rows={history} />
                <div className="mt-1">
                  <ComponentsKey />
                </div>
              </div>
            )}

            <div className="mt-4 border-t border-line pt-3">
              <SectionTitle>If it carries on</SectionTitle>
              <ProjectionInputs />

              {projection && (
                <>
                  {visible && (
                    <div className="mt-3">
                      <div className="mb-1 flex items-center justify-between">
                        <span className="text-[10px] text-ink-soft">
                          {view === "people" ? "People" : "Households"} to {s.toYear}
                        </span>
                        <div className="flex gap-1">
                          {(["people", "households"] as const).map((v) => (
                            <button
                              key={v}
                              onClick={() => setView(v)}
                              className={`rounded border px-1.5 py-0.5 text-[10px] transition-colors ${
                                view === v
                                  ? "border-accent text-accent"
                                  : "border-line text-ink-soft hover:bg-surface-alt"
                              }`}
                            >
                              {v === "people" ? "People" : "Homes"}
                            </button>
                          ))}
                        </div>
                      </div>
                      <PopulationChart projection={projection} view={view} />
                    </div>
                  )}

                  <div className="mt-2 grid grid-cols-3 gap-2">
                    <Figure
                      label={`People ${projection.final.year}`}
                      value={countShort(projection.final.pop)}
                      sub={`${countShort(projection.final.low)} – ${countShort(projection.final.high)}`}
                      accent
                    />
                    <Figure
                      label="Households"
                      value={countShort(projection.final.households)}
                      sub={`from ${countShort(projection.base.main_res)}`}
                    />
                    <Figure
                      label="Homes / yr"
                      value={
                        (projection.dwellingsPerYear >= 0 ? "+" : "−") +
                        num(Math.abs(Math.round(projection.dwellingsPerYear)))
                      }
                      sub="implied demand"
                    />
                  </div>

                  <p className="mt-2 text-[10px] leading-snug text-ink-soft">
                    {data.department.name} follows INSEE's {SCENARIO_LABELS[s.scenario].toLowerCase()}{" "}
                    scenario;{" "}
                    {data.scope === "departement"
                      ? "the department is projected directly."
                      : `this area has been running ${pct(projection.drift)}/yr against it, faded out over twenty years.`}{" "}
                    Households fall out of the population path and the local
                    household size, which has been shrinking for fifty years.
                  </p>
                </>
              )}
            </div>
          </>
        )}
      </div>

      <div className="mt-4 border-t border-line pt-3">
        <SectionTitle>Does it show up in prices?</SectionTitle>

        <div className="grid grid-cols-2 gap-2">
          <Select
            label="Compare"
            value={s.correlationX}
            options={X_METRICS.map((m) => ({ value: m.id, label: m.label }))}
            onChange={s.setCorrelationX}
          />
          <Select
            label="Against"
            value={s.correlationY}
            options={Y_METRICS.map((m) => ({ value: m.id, label: m.label }))}
            onChange={s.setCorrelationY}
          />
        </div>
        <div className="mt-2">
          <Select
            label="Cities in"
            value={s.depFilter}
            options={DEPARTMENTS}
            onChange={s.setDepFilter}
          />
        </div>
        <div className="mt-2">
          <Choice
            label="Smallest city included"
            value={s.minPop}
            options={[
              { value: 0, label: "Any" },
              { value: 5000, label: "5 000+" },
              { value: 20000, label: "20 000+" },
            ]}
            onChange={s.setMinPop}
          />
        </div>

        {visible && cross.points.length > 0 && (
          <div className="mt-3">
            <CorrelationScatter
              points={cross.points}
              x={s.correlationX}
              y={s.correlationY}
              selectedCode={data?.code ?? null}
            />
          </div>
        )}

        <p className="mt-1 text-[11px] leading-snug">
          Across {cross.stats.n} cities, {mx.axis.split(",")[0]} and{" "}
          {my.axis.split(",")[0]} show{" "}
          <strong className="font-semibold">
            {strengthLabel(cross.stats.rho)}
          </strong>{" "}
          {cross.stats.rho !== null &&
            cross.stats.rho !== 0 &&
            Math.abs(cross.stats.rho) >= 0.1 &&
            (cross.stats.rho > 0 ? "positive " : "negative ")}
          agreement
          {cross.stats.r !== null && cross.stats.rho !== null && (
            <span className="text-ink-soft tabular-nums">
              {" "}
              (r {cross.stats.r.toFixed(2)}, ρ {cross.stats.rho.toFixed(2)})
            </span>
          )}
          .
        </p>

        {here && (
          <p className="mt-1 text-[10px] leading-snug text-ink-soft">
            {here.name}: {mx.format(here[s.correlationX])} ·{" "}
            {my.format(here[s.correlationY])}, from {num(here.sales)} recorded
            sales.
          </p>
        )}
      </div>

      <p className="mt-4 border-t border-line pt-2 text-[10px] leading-snug text-ink-soft">
        A correlation across cities, not a claim about any one of them — and the
        census is a rolling survey, so it cannot see a turn in a single year.
      </p>
    </div>
  );
}

/** What the map is shaded by. Kept at the top: it changes what you are looking at. */
function MapControls({ metric, period }: { metric: MapMetricId; period: number }) {
  const setMetric = useDemographicsStore((s) => s.setMetric);
  const setPeriod = useDemographicsStore((s) => s.setPeriod);
  const isVisible = useLayerStore((s) => s.isVisible);
  const setVisible = useLayerStore((s) => s.setVisible);
  const unavailable = useLayerStore((s) => s.unavailable);

  const shown = isVisible("demographics");
  const missing = unavailable.includes("demographics");
  const periodic = MAP_METRICS[metric].periodic;

  return (
    <section>
      <div className="grid grid-cols-2 gap-2">
        <Select
          label="Map shows"
          value={metric}
          options={Object.entries(MAP_METRICS).map(([id, m]) => ({
            value: id as MapMetricId,
            label: m.label,
          }))}
          onChange={setMetric}
        />
        <Select
          label="Period"
          value={period}
          options={CENSUS_PERIODS.map((p) => ({
            value: p.to,
            label: periodLabel(p.to),
          }))}
          onChange={setPeriod}
          hint={
            periodic
              ? undefined
              : "This measure is a stock at the latest census, so the period does not apply to it."
          }
        />
      </div>
      {missing ? (
        <p className="mt-1.5 text-[10px] text-ink-soft">
          Census tiles not built — run <code>make demographics</code>.
        </p>
      ) : (
        !shown && (
          <button
            onClick={() => setVisible("demographics", true)}
            className="mt-1.5 text-[10px] font-medium text-accent hover:underline"
          >
            Show it on the map
          </button>
        )
      )}
    </section>
  );
}

function ProjectionInputs() {
  const scenario = useDemographicsStore((s) => s.scenario);
  const persistence = useDemographicsStore((s) => s.persistence);
  const toYear = useDemographicsStore((s) => s.toYear);
  const setScenario = useDemographicsStore((s) => s.setScenario);
  const setPersistence = useDemographicsStore((s) => s.setPersistence);
  const setToYear = useDemographicsStore((s) => s.setToYear);

  return (
    <div className="space-y-2.5">
      <div className="grid grid-cols-2 gap-2">
        <Select
          label="Horizon"
          value={toYear}
          options={HORIZON_YEARS.map((y) => ({ value: y, label: String(y) }))}
          onChange={setToYear}
        />
        <Select
          label="INSEE scenario"
          value={scenario}
          options={Object.entries(SCENARIO_LABELS).map(([id, label]) => ({
            value: id as ProjectionScenario,
            label,
          }))}
          onChange={setScenario}
          hint="Omphale variants, published at department level and 2018-based."
        />
      </div>
      <div>
        <div className="flex items-baseline justify-between">
          <label
            htmlFor="demographics-persistence"
            className="text-[10px] font-medium uppercase tracking-wider text-ink-soft"
            title="How much of this area's recent difference from its department is carried forward."
          >
            Keeps its own trend
          </label>
          <span className="text-sm font-semibold tabular-nums">
            {Math.round(persistence * 100)}%
          </span>
        </div>
        <input
          id="demographics-persistence"
          type="range"
          min={0}
          max={100}
          step={5}
          value={Math.round(persistence * 100)}
          onChange={(e) => setPersistence(Number(e.target.value) / 100)}
          className="mt-1 w-full accent-accent"
        />
      </div>
    </div>
  );
}


